import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { toast } from '@/hooks/use-toast';

interface AdminContextProps {
  isAdmin: boolean;
  isImpersonating: boolean;
  originalAdmin: any | null;
  targetUser: { id: string; email: string } | null;
  startImpersonation: (targetUserId: string, reason?: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  loading: boolean;
}

const AdminContext = createContext<AdminContextProps>({
  isAdmin: false,
  isImpersonating: false,
  originalAdmin: null,
  targetUser: null,
  startImpersonation: async () => {},
  endImpersonation: async () => {},
  loading: false,
});

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdmin, setOriginalAdmin] = useState<any | null>(null);
  const [targetUser, setTargetUser] = useState<{ id: string; email: string } | null>(null);
  const [loading, setLoading] = useState(false);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc('is_admin', { user_id: user.id });
        if (!error) {
          setIsAdmin(data || false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    checkAdminStatus();
  }, [user]);

  // Check if currently impersonating (server-side only - no localStorage)
  useEffect(() => {
    const checkImpersonationStatus = async () => {
      if (!user || !isAdmin) return;
      
      try {
        console.log('[AdminContext] Checking impersonation status for admin:', user.id);
        const { data, error } = await supabase
          .from('admin_impersonation_log')
          .select('target_user_id')
          .eq('admin_user_id', user.id)
          .is('ended_at', null)
          .order('started_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (data && !error) {
          console.log('[AdminContext] Active impersonation found:', data.target_user_id);
          // Since we can't access auth.users directly, we'll use the edge function to get user details
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) {
            const response = await supabase.functions.invoke('admin-impersonate', {
              body: { action: 'check', targetUserId: data.target_user_id },
              headers: { Authorization: `Bearer ${session.access_token}` }
            });
            
            if (response.data?.targetUser) {
              console.log('[AdminContext] Setting impersonation state for:', response.data.targetUser.email);
              setTargetUser(response.data.targetUser);
              setIsImpersonating(true);
              setOriginalAdmin(user);
            }
          }
        } else {
          console.log('[AdminContext] No active impersonation found');
        }
      } catch (error) {
        console.error('[AdminContext] Failed to check impersonation status:', error);
      }
    };

    checkImpersonationStatus();
  }, [user, isAdmin]);

  const startImpersonation = async (targetUserId: string, reason = 'Customer support') => {
    if (!isAdmin) {
      toast({
        title: "Access denied",
        description: "You don't have admin privileges",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Get current session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No active session');
      }

      // Call Edge Function to handle impersonation server-side
      const response = await supabase.functions.invoke('admin-impersonate', {
        body: { action: 'start', targetUserId, reason },
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to start impersonation');
      }

      const result = response.data;
      if (!result.success) {
        throw new Error(result.error || 'Failed to start impersonation');
      }

      setIsImpersonating(true);
      setOriginalAdmin(user);
      setTargetUser(result.targetUser);

      toast({
        title: "Viewing as user",
        description: `Now viewing as ${result.targetUser.email}. You have access to their organizations.`,
      });

      // Redirect to dashboard to see the user's content
      window.location.href = '/dashboard';
    } catch (error: any) {
      console.error('Error starting impersonation:', error);
      toast({
        title: "Failed to start viewing",
        description: error.message || "Could not access user's account",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const endImpersonation = async () => {
    setLoading(true);
    try {
      // Get current session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No active session');
      }

      // Call Edge Function to end impersonation and clean up temp memberships
      const response = await supabase.functions.invoke('admin-impersonate', {
        body: { action: 'end' },
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to end session');
      }

      const result = response.data;
      if (!result.success) {
        throw new Error(result.error || 'Failed to end session');
      }

      setIsImpersonating(false);
      setOriginalAdmin(null);
      setTargetUser(null);

      toast({
        title: "Session ended",
        description: "Returned to admin view",
      });

      // Redirect to admin panel
      window.location.href = '/admin';
    } catch (error: any) {
      console.error('Error ending impersonation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to end session properly",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminContext.Provider
      value={{
        isAdmin,
        isImpersonating,
        originalAdmin,
        targetUser,
        startImpersonation,
        endImpersonation,
        loading,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
